import type { Dispatch, SetStateAction } from 'react';
import { toast as sonnerToast } from 'sonner';
import type { HealthCheckFinding } from '@/types/healthCheck';
import type {
  TimesheetDeadlineSettings as DeadlineSettingsType,
  UtilisationSettings as UtilisationSettingsType,
  PolicyLimits,
} from '@/types/timesheetSettings';

export type ToastApi = typeof sonnerToast;

export interface ApplyHealthCheckFixDeps {
  deadlineSettings: DeadlineSettingsType | null;
  setDeadlineSettings: Dispatch<SetStateAction<DeadlineSettingsType | null>>;
  utilisationSettings: UtilisationSettingsType | null;
  setUtilisationSettings: Dispatch<SetStateAction<UtilisationSettingsType | null>>;
  policyLimits: PolicyLimits | undefined;
  setPolicyLimits: Dispatch<SetStateAction<PolicyLimits | undefined>>;
  setShowRole: Dispatch<SetStateAction<boolean>>;
  setEnableCopyPreviousWeek: Dispatch<SetStateAction<boolean>>;
  setHistoricalEntryLimit: Dispatch<SetStateAction<number>>;
  setAllowNegativeHours: Dispatch<SetStateAction<boolean>>;
  setEnableAiInsights: Dispatch<SetStateAction<boolean>>;
  setMinimumEntryDuration: Dispatch<SetStateAction<'6min' | '15min' | '30min' | '1hour'>>;
  setAllowFutureDatedEntries: Dispatch<SetStateAction<'no' | '1week' | '2weeks' | '4weeks' | 'unlimited'>>;
  markTabAsChanged: (tabId: string) => void;
  setActiveTab: Dispatch<SetStateAction<string>>;
  setShowHealthCheckDialog: Dispatch<SetStateAction<boolean>>;
  toast: ToastApi;
}

/** Applies a health check quick fix to the relevant settings field and navigates to its tab. */
export const applyHealthCheckFix = (finding: HealthCheckFinding, deps: ApplyHealthCheckFixDeps): void => {
  const {
    deadlineSettings,
    setDeadlineSettings,
    utilisationSettings,
    setUtilisationSettings,
    policyLimits,
    setPolicyLimits,
    setShowRole,
    setEnableCopyPreviousWeek,
    setHistoricalEntryLimit,
    setAllowNegativeHours,
    setEnableAiInsights,
    setMinimumEntryDuration,
    setAllowFutureDatedEntries,
    markTabAsChanged,
    setActiveTab,
    setShowHealthCheckDialog,
    toast,
  } = deps;

  if (!finding.fixAction) return;

  const { field, value } = finding.fixAction;

  // Apply the fix based on the field
  switch (field) {
    case 'deadlineDay':
      if (deadlineSettings && typeof value === 'object' && 'day' in value && 'time' in value) {
        setDeadlineSettings({
          ...deadlineSettings,
          deadlineDay: value.day,
          deadlineTime: value.time,
        });
        markTabAsChanged('submissions-deadlines');
      }
      break;
    case 'targetUtilisationRate':
      if (utilisationSettings && typeof value === 'number') {
        setUtilisationSettings({
          ...utilisationSettings,
          targetUtilisationRate: value,
        });
        markTabAsChanged('utilisation');
      }
      break;
    case 'legalMaxHoursPerWeek':
      if (policyLimits && typeof value === 'number') {
        setPolicyLimits({
          ...policyLimits,
          legalMaxHoursPerWeek: value,
        });
        markTabAsChanged('working-hours');
      }
      break;
    case 'gracePeriod':
      if (deadlineSettings && typeof value === 'number') {
        setDeadlineSettings({
          ...deadlineSettings,
          gracePeriodDays: value,
        });
        markTabAsChanged('submissions-deadlines');
      }
      break;
    case 'showDescription':
    case 'showRole':
    case 'showProject':
      if (typeof value === 'boolean') {
        if (field === 'showDescription') {
          // Note: showDescription is not currently in state, would need to add
          toast.warning('This fix requires additional implementation');
        } else if (field === 'showRole') {
          setShowRole(value);
          markTabAsChanged('general');
        } else if (field === 'showProject') {
          // Note: showProject is not currently in state, would need to add
          toast.warning('This fix requires additional implementation');
        }
      }
      break;
    case 'enableCopyPreviousWeek':
      if (typeof value === 'boolean') {
        setEnableCopyPreviousWeek(value);
        markTabAsChanged('general');
      }
      break;
    case 'historicalEntryLimit':
      if (typeof value === 'number') {
        setHistoricalEntryLimit(value);
        markTabAsChanged('general');
      }
      break;
    case 'allowNegativeHours':
      if (typeof value === 'boolean') {
        setAllowNegativeHours(value);
        markTabAsChanged('general');
      }
      break;
    case 'enableAIInsights':
      if (typeof value === 'boolean') {
        setEnableAiInsights(value);
        markTabAsChanged('general');
      }
      break;
    case 'minimumDuration':
      if (typeof value === 'number') {
        // Convert number to the enum type
        const durationMap: Record<number, '6min' | '15min' | '30min' | '1hour'> = {
          0.1: '6min',
          0.25: '15min',
          0.5: '30min',
          1: '1hour',
        };
        const duration = durationMap[value];
        if (duration) {
          setMinimumEntryDuration(duration);
          markTabAsChanged('general');
        }
      }
      break;
    case 'futureEntryLimit':
      if (typeof value === 'number') {
        const futureMap: Record<number, 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited'> = {
          0: 'no',
          1: '1week',
          2: '2weeks',
          4: '4weeks',
        };
        const future = futureMap[value];
        if (future) {
          setAllowFutureDatedEntries(future);
          markTabAsChanged('general');
        }
      }
      break;
    case 'legalMaxHoursPerDay':
      if (policyLimits && typeof value === 'number') {
        setPolicyLimits({
          ...policyLimits,
          legalMaxHoursPerDay: value,
        });
        markTabAsChanged('working-hours');
      }
      break;
    case 'sendReminders':
      if (deadlineSettings && typeof value === 'boolean') {
        setDeadlineSettings({
          ...deadlineSettings,
          sendReminders: value,
        });
        markTabAsChanged('submissions-deadlines');
      }
      break;
    default:
      toast.warning('Quick fix not available for this setting');
      return;
  }

  // Navigate to the affected tab if specified
  if (finding.affectedTab) {
    setActiveTab(finding.affectedTab);
  }

  toast.success('Fix applied', {
    description: 'Remember to save your changes',
  });

  // Close the health check dialog
  setShowHealthCheckDialog(false);
};
